/**
 * useAiVideoAnalysis
 *
 * Drives an AI metadata analysis job and exposes a small state machine to the
 * UI. Deliberately owns *no* form knowledge — it just runs the service, tracks
 * progress/stage, and hands back the normalized result. The Add Content
 * container decides what to do with that result (auto-fill, badge fields, etc).
 * This separation is what keeps the AI processing service independent of the
 * form logic.
 *
 * Status lifecycle:
 *   idle → running → complete
 *                 ↳ failed  (retry via analyze())
 *                 ↳ idle    (reset())
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { runVideoAnalysis, ANALYSIS_STAGES } from "../services/ai";

export const AI_STATUS = {
  IDLE: "idle",
  RUNNING: "running",
  COMPLETE: "complete",
  FAILED: "failed",
};

export default function useAiVideoAnalysis() {
  const [status, setStatus] = useState(AI_STATUS.IDLE);
  const [activeStage, setActiveStage] = useState(null);
  const [progress, setProgress] = useState(0);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  const abortRef = useRef(null);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      // Cancel any in-flight job when the form unmounts.
      abortRef.current?.abort();
    };
  }, []);

  const reset = useCallback(() => {
    abortRef.current?.abort();
    abortRef.current = null;
    setStatus(AI_STATUS.IDLE);
    setActiveStage(null);
    setProgress(0);
    setResult(null);
    setError(null);
  }, []);

  /**
   * Start (or restart) an analysis for the given video.
   * @param {object} video      Selected video descriptor.
   * @param {object} [overrides] Passed through to the service (test hooks).
   */
  const analyze = useCallback((video, overrides = {}) => {
    // Supersede any running job — the newest selection wins.
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    setStatus(AI_STATUS.RUNNING);
    setError(null);
    setResult(null);
    setProgress(0);
    setActiveStage(ANALYSIS_STAGES[0]?.id ?? null);

    runVideoAnalysis({
      video,
      signal: controller.signal,
      overrides,
      onStage: (stageId, pct) => {
        if (!mountedRef.current || controller.signal.aborted) return;
        if (stageId !== "complete") setActiveStage(stageId);
        if (typeof pct === "number") setProgress(pct);
      },
    })
      .then((res) => {
        if (!mountedRef.current || controller.signal.aborted) return;
        setResult(res);
        setProgress(100);
        setActiveStage("complete");
        setStatus(AI_STATUS.COMPLETE);
      })
      .catch((err) => {
        // Aborts are intentional (reselection/unmount) — not surfaced as errors.
        if (err?.name === "AbortError") return;
        if (!mountedRef.current) return;
        setError(err?.message || "AI analysis failed. You can retry or continue manually.");
        setStatus(AI_STATUS.FAILED);
      });

    return controller;
  }, []);

  return {
    status,
    stages: ANALYSIS_STAGES,
    activeStage,
    progress,
    result,
    error,
    analyze,
    reset,
    isIdle: status === AI_STATUS.IDLE,
    isRunning: status === AI_STATUS.RUNNING,
    isComplete: status === AI_STATUS.COMPLETE,
    isFailed: status === AI_STATUS.FAILED,
  };
}
